import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';

interface SubmitResult {
  refCode: string | null;
  duplicate: boolean;
  message: string;
}

const CATEGORIES = [
  'ค่าครองชีพ / อาหาร',
  'ค่ารักษาพยาบาล',
  'ซ่อมแซมที่อยู่อาศัย',
  'ทุนการศึกษาเด็ก',
  'ผู้สูงอายุ / ผู้ป่วยติดเตียง',
  'ประสบภัยพิบัติ',
  'อื่น ๆ',
];

/**
 * ยื่นขอความช่วยเหลือ — ไม่ต้องล็อกอิน
 *
 * คนที่มาขอความช่วยเหลือมักเป็นผู้สูงอายุและใช้มือถือร่วมกับลูกหลาน
 * ถ้าบังคับผูกบัญชีก่อน เขาจะไม่ได้ยื่นจริง — ฟอร์มนี้จึงขอเท่าที่จำเป็น
 * และไม่มีขั้นตอนยืนยันตัวตนใด ๆ
 */
export default function HelpRequest() {
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [addressText, setAddressText] = useState('');
  const [category, setCategory] = useState('');
  const [detail, setDetail] = useState('');
  const [consent, setConsent] = useState(false);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SubmitResult | null>(null);

  const detailOk = detail.trim().length >= 10;
  const canSubmit = contactName.trim() && detailOk && consent && !busy;

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const res = await api<SubmitResult>('/help-requests', {
        method: 'POST',
        anonymous: true,
        idempotent: true,
        body: {
          contactName: contactName.trim(),
          contactPhone: contactPhone.trim() || undefined,
          addressText: addressText.trim() || undefined,
          category: category || undefined,
          detail: detail.trim(),
          photoKeys: [],
          consentData: true,
        },
      });
      setResult(res);
    } catch (e) {
      setError(
        (e as { message?: string }).message ?? 'ส่งคำขอไม่สำเร็จ กรุณาลองใหม่อีกครั้ง',
      );
    } finally {
      setBusy(false);
    }
  }

  if (result) {
    return (
      <div className="screen">
        <div className="topbar">
          <h1>ส่งคำขอเรียบร้อย</h1>
        </div>

        <div className="card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 44 }}>🙏</div>
          <p>{result.message}</p>

          {result.refCode && (
            <>
              <p className="muted" style={{ marginBottom: 4 }}>
                รหัสอ้างอิงของท่าน
              </p>
              <div
                className="mono"
                style={{ fontSize: 26, fontWeight: 700, letterSpacing: 1, margin: '4px 0 10px' }}
              >
                {result.refCode}
              </div>
              {/* ย้ำให้จดเพราะหน้านี้ปิดแล้วเปิดกลับมาดูไม่ได้ */}
              <p className="muted" style={{ fontSize: 14 }}>
                <strong>กรุณาจดหรือถ่ายรูปหน้าจอนี้ไว้</strong>
                <br />
                ใช้รหัสนี้ติดตามสถานะได้ตลอด โดยไม่ต้องล็อกอิน
              </p>
            </>
          )}
        </div>

        <Link className="btn block" to={`/track?code=${result.refCode ?? ''}`}>
          ดูสถานะคำขอ
        </Link>
      </div>
    );
  }

  return (
    <div className="screen">
      <div className="topbar">
        <h1>ขอความช่วยเหลือ</h1>
      </div>

      {error && <div className="alert error">{error}</div>}

      <div className="card">
        <p className="muted" style={{ marginTop: 0 }}>
          กรอกข้อมูลเท่าที่สะดวก เจ้าหน้าที่จะติดต่อกลับเพื่อสอบถามเพิ่มเติมและนัดลงพื้นที่
        </p>

        <div className="field">
          <label htmlFor="name">ชื่อผู้ติดต่อ *</label>
          <input
            id="name"
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            placeholder="ชื่อ-นามสกุล"
          />
        </div>

        <div className="field">
          <label htmlFor="phone">เบอร์โทรที่ติดต่อได้</label>
          <input
            id="phone"
            type="tel"
            inputMode="numeric"
            value={contactPhone}
            onChange={(e) => setContactPhone(e.target.value)}
            placeholder="08xxxxxxxx"
          />
          <div className="hint">ไม่มีเบอร์ก็ยื่นได้ แต่เจ้าหน้าที่จะติดต่อกลับได้ช้ากว่า</div>
        </div>

        <div className="field">
          <label htmlFor="addr">ที่อยู่ที่จะให้ไปเยี่ยม</label>
          <textarea
            id="addr"
            rows={2}
            value={addressText}
            onChange={(e) => setAddressText(e.target.value)}
            placeholder="บ้านเลขที่ หมู่ ตำบล อำเภอ จังหวัด"
          />
        </div>

        <div className="field">
          <label htmlFor="cat">เรื่องที่ขอความช่วยเหลือ</label>
          <select id="cat" value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">— เลือก —</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="detail">เล่าสถานการณ์ให้เราฟัง *</label>
          <textarea
            id="detail"
            rows={5}
            value={detail}
            onChange={(e) => setDetail(e.target.value)}
            placeholder="เช่น อยู่กับหลาน 2 คน รายได้ไม่พอค่าอาหาร หลังคารั่วเวลาฝนตก"
          />
          {!detailOk && detail.length > 0 && (
            <div className="hint">เล่าเพิ่มอีกนิดนะคะ อย่างน้อย 10 ตัวอักษร</div>
          )}
        </div>

        <label className="checkbox">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
          <span>
            ยินยอมให้มูลนิธิเก็บและใช้ข้อมูลนี้เพื่อพิจารณาให้ความช่วยเหลือ
            และติดต่อกลับหาท่าน
          </span>
        </label>
      </div>

      <button className="btn block" disabled={!canSubmit} onClick={() => void submit()}>
        {busy ? 'กำลังส่ง…' : 'ส่งคำขอ'}
      </button>

      <Link className="btn block secondary" to="/track" style={{ marginTop: 8 }}>
        เคยยื่นแล้ว — ดูสถานะ
      </Link>
    </div>
  );
}
